/**
 * The creator's vocabulary: what one configured position is, when it is
 * complete, and what it turns into.
 *
 * `/pl/kreator` is the ONLY place on the site where something is added to the
 * basket. Every offer page hands off to it with a deep link rather than
 * carrying its own picker, which is why this file exists at all: the rules for
 * "does this need a date / a range / an hour / a city" have exactly one
 * implementation, and it is driven by the catalogue's capability columns
 * (`bookingMode`, `coverageMode`) rather than by which page the visitor came
 * from. Adding a kind of offer adds no rule here.
 *
 * Dates are `YYYY-MM-DD` strings throughout — see `lib/dates.ts` for why a
 * `Date` must never travel.
 */
// Narrow subpaths, never the barrel. `@repo/core`'s index re-exports every
// service, each service imports its schemas, and schemas import zod — so one
// `bandAt` from the barrel put three hundred kilobytes of validation library
// into the creator's browser bundle. This module is read by client components;
// what it imports, they ship.
import { bandAt, paidMinutesFor } from '@repo/core/film-pricing'
import { footageLimitFor, footageOverageNetMinor } from '@repo/core/footage-limit'
import type {
  BookingMode,
  BriefMode,
  CoverageMode,
  PricingUnit,
  SourceMode,
} from '@repo/core/mappers'
import { isSourceUrl } from '@repo/core/material'
import { RENTAL_TERMS_TEXT, termLabel, termOfSlug } from '@repo/core/rental-terms'
import { DEFAULT_PRICING_RULES, type PricingRuleValues, ruleValue } from '@repo/db/pricing-rules'
import { dayStarts, isShootDayLevel, SHOOT_DAY_HOURS } from '@repo/db/shoot-day'
import { addDaysISO, fromISO } from '@/lib/dates'
import type { CartLineRef } from '@/lib/order-line'
import { countDays, lastHeldDay, type RentalTermView } from '@/lib/rental'
import type { PlanView } from '@/lib/services'
import type { ShootPricingView } from '@/lib/shoot-pricing'

/** A variant, plus how long it occupies a calendar. */
export type CreatorVariantView = PlanView & {
  /** Minutes one unit of this variant takes; `null` when it books nothing. */
  durationMinutes: number | null
  /**
   * The soonest day this level can still be started, `YYYY-MM-DD` (spec §5).
   *
   * Read from the capacity calendar with the 18:00 cutoff already applied, so
   * it is the date the customer would really get rather than "tomorrow" said
   * hopefully. Three states, all of them meaningful:
   *
   * `undefined` — this level has no calendar (a hire, a room, a quote). Nothing
   * to print.
   * `null` — asked, and booked out for the whole horizon.
   * a day — that day.
   */
  readyFrom?: string | null
}

/** One city the offer travels to. */
export type CreatorCityView = { slug: string; name: string }

/**
 * Anything orderable, described by what it NEEDS rather than by what it is.
 * The creator renders a card from this and nothing else, so a camera, a room,
 * an operator and a montage package all take the same code path.
 */
/** One published opening, in Warsaw wall-clock minutes past midnight. */
export type OpenDay = { day: string; from: number; to: number }

export type CreatorOfferingView = {
  slug: string
  name: string
  blurb: string
  /** The marketing page selling it — where "zobacz zakres" goes. */
  href: string
  /** The offer page it belongs to, for grouping the picker. */
  pageSlug: string
  pageLabel: string
  /**
   * The narrower shelf it sits on — "Światło", "Obiektywy", "Statywy".
   *
   * Equal to {@link pageLabel} for anything the catalogue files directly under
   * a page, which is everything except the hire items. It exists for the
   * search box: eleven cameras and lamps all carry the page label "Wynajem",
   * so without this, typing "swiatlo" or "statyw" — the words somebody
   * actually reaches for — matched nothing at all.
   */
  categoryLabel: string
  pricingUnit: PricingUnit
  bookingMode: BookingMode
  coverageMode: CoverageMode
  /** Whether this asks the buyer to describe the work, and how hard the ask is. */
  briefMode: BriefMode
  /** Whether it asks for the buyer's own raw material, and how hard (spec §7). */
  sourceMode: SourceMode
  /** Shortest bookable block for `TIME_WINDOW`, in minutes. */
  minBookingMinutes: number | null
  /**
   * The days this room is open, and between which hours (spec §10 · I1).
   *
   * `null` for everything that is not booked by the clock. An EMPTY array is
   * not the same thing and never means "open always": it is a room whose
   * calendar nobody has filled in, and the server refuses every window against
   * it — which is the only reading that makes a published calendar mean
   * anything. See `isWithinOpenHours` in `@repo/core`.
   */
  openDays: OpenDay[] | null
  /** Polish plural forms of the unit: 1 / 2–4 / 5+. */
  unitForms: [string, string, string]
  quantityLabel: string
  defaultQty: number
  /** Per-piece work, so the volume ladder applies to a line of it. */
  isVolumeEligible: boolean
  /** Open on the creator by default instead of waiting behind the picker. */
  featured: boolean
  variants: CreatorVariantView[]
  /** City slugs it travels to. Empty unless `coverageMode` is `CITIES`. */
  citySlugs: string[]
  /**
   * The owner's photography model, for the one offering it prices.
   *
   * `null` for everything else, which is almost everything — a camera hire and
   * an edit are priced by their variant and have no rate card. Carried ON the
   * offering rather than passed beside it so that `lineMoney` can price a shoot
   * with the arguments it already takes: the estimate on the screen and the
   * figure the checkout freezes then come out of one function, which is the
   * whole reason the basket stopped disagreeing with the site.
   */
  shoot: ShootPricingView | null
}

/** What the visitor has filled in for one position. */
export type CreatorPick = {
  variantSlug: string | null
  qty: number
  /** Start day, or the only day. */
  from: string
  /** End day — `DATE_RANGE` only. */
  to: string
  /** Start time `HH:MM` — `TIME_WINDOW` only. */
  time: string
  citySlug: string
  /** What the customer wants made — spec §6. Empty unless the offering asks. */
  brief: string
  /** Where the raw material is — spec §7. Empty unless the offering asks. */
  sourceUrl: string
  /** Which length band a film sits on — spec §8. `null` unless priced by the minute. */
  lengthBand: number | null
  /** Declared minutes of raw footage — spec §8. Empty unless the offering asks. */
  footageMinutes: string
  /**
   * The basket line this one is cut from — "rolki z tego filmu" (spec §8 · G8).
   *
   * The one field here that points at ANOTHER line rather than describing this
   * one, which is why it is a `lineId` and not a slug: two films of the same
   * level in one order are two different answers. `null` for everything that
   * stands on its own, which is almost everything.
   */
  fromFilmLineId: string | null
  /**
   * WHAT THE SHOOT IS — the three answers `/pl/fotograf-operator` collected.
   *
   * Empty for everything that is not a photography day. Together they are what
   * turns a day into a price: the session picks the rate card, the scope picks
   * its row, and the photo count moves along the row. Without them the basket
   * prices a shoot at the level's day rate, which is what it used to do and
   * what made the site quote 2 450 zł and the basket charge 400.
   *
   * STRINGS, including the count, for the same reason `footageMinutes` is one:
   * they arrive from a URL and from a form, and an empty box is a question
   * nobody answered rather than a zero.
   */
  shootSession: string
  shootScope: string
  shootPhotos: string
  /**
   * WHAT IS BEING RECORDED — the operator's one answer, where the photographer
   * gives three.
   *
   * A `recording_type` slug, empty for everything that is not a recording day.
   * It replaced the plan the operator used to be booked in: „cały plan" told
   * you how long somebody was there and nothing about what they were doing, and
   * the owner sells the second thing.
   */
  recordingType: string
}

/**
 * The quantity a variant opens at.
 *
 * `defaultQuantity` describes the offering's BASE unit — "Ile godzin?" starts
 * at two because nobody books a room for one. A longer block is already a whole
 * booking, so it opens at one: two "cały dzień" is two days in the room, which
 * is not what someone clicking the day rate meant to ask for.
 */
export function defaultQtyFor(
  offering: CreatorOfferingView,
  variant: CreatorVariantView | undefined,
): number {
  const base = offering.minBookingMinutes
  if (variant?.durationMinutes && base && variant.durationMinutes > base) return 1
  return offering.defaultQty
}

export function emptyPick(offering: CreatorOfferingView): CreatorPick {
  // One variant is not a choice — preselect it, or every camera on the page
  // opens asking which of its one options the visitor wants.
  const only = offering.variants.length === 1 ? offering.variants[0] : undefined
  return {
    variantSlug: only?.slug ?? null,
    qty: defaultQtyFor(offering, only),
    from: '',
    to: '',
    time: '',
    // Same rule for a single city: no question worth asking.
    citySlug: offering.citySlugs.length === 1 ? (offering.citySlugs[0] ?? '') : '',
    brief: '',
    sourceUrl: '',
    // Opens on "20–30 min", the band the spec itself works its example in and
    // the one a typical long-form actually lands in. A slider that starts at
    // the cheapest rung would be quoting a price nobody asked for.
    lengthBand: needsLengthBand(offering) ? DEFAULT_LENGTH_BAND : null,
    // A STRING, unlike every other number here, because it is typed into a box
    // and an empty box is not zero minutes of footage — it is a question nobody
    // has answered yet, and `pickProblem` has to be able to tell them apart.
    footageMinutes: '',
    // Nothing is cut from anything by default (spec §8 · G8). The tick only
    // exists on the order screen, where the other lines of the basket are
    // visible — the creator configures one offering at a time and has nothing
    // to point at.
    fromFilmLineId: null,
    // Only `/pl/fotograf-operator` can fill these — the creator does not ask
    // „ile osób przed obiektywem?" and is not going to. A shoot configured
    // anywhere else is priced by its plan, exactly as before.
    shootSession: '',
    shootScope: '',
    shootPhotos: '',
    recordingType: '',
  }
}

/** Which rung the film slider opens on. */
export const DEFAULT_LENGTH_BAND = 3

// ── what a given offering asks for ─────────────────────────────────────────

export const needsDate = (o: CreatorOfferingView) => o.bookingMode !== 'NONE'
export const needsRange = (o: CreatorOfferingView) => o.bookingMode === 'DATE_RANGE'
/**
 * Does this line need an hour, not just a day?
 *
 * ── WHY A SHOOT DAY NOW DOES ─────────────────────────────────────────────
 * It always did in real life — a photographer stands somewhere from ten until
 * six — and the shop threw the answer away: `/pl/fotograf-operator` asked for
 * a start time in step 3 and the basket link carried only the date, so the
 * booking was written `isAllDay` and ran midnight to midnight.
 *
 * That was survivable while nothing downstream read the clock. It stopped
 * being survivable with `performer_availability`: `windowFor` in
 * `assignment.service.ts` hands the booking's own `startsAt`/`endsAt` to
 * `isSpanDeclared`, so an all-day shoot asks every photographer whether they
 * declared midnight-to-midnight. Nobody ever will, so the one job type that
 * HAS hours was the one the new availability check could never place.
 *
 * With an hour on the line, `refFrom` produces the same `YYYY-MM-DDTHH:MM`
 * window a studio hour already uses, `orderLinePeriod` builds a real
 * `clockPeriod`, and the declared-hours test finally compares like with like.
 *
 * Only a line on the DAY level. A shoot offering with no such variant picked
 * is still a quote, and quotes carry no window.
 */
export const needsTime = (o: CreatorOfferingView, pick?: CreatorPick) =>
  o.bookingMode === 'TIME_WINDOW' ||
  (o.bookingMode === 'DAY' && isShootDayLevel(pick?.variantSlug ?? null))
export const needsCity = (o: CreatorOfferingView) => o.coverageMode === 'CITIES'

/** What this room publishes for one day, or `null` when it is shut. */
export function openDayFor(offering: CreatorOfferingView, day: string): OpenDay | null {
  return offering.openDays?.find((row) => row.day === day) ?? null
}

/**
 * The start times a booking of `hours` can actually begin at, on this day.
 *
 * Whole hours only (spec §10: "pełne godziny"), and only those that leave room
 * for the whole booking inside the published window — offering 19:00 on a room
 * that shuts at 20:00 to somebody who asked for three hours is a control that
 * produces a value the server then refuses.
 *
 * This replaced a hardcoded 08:00–20:00 half-hour list whose own comment said
 * it was a stand-in "until opening hours land on the offering". They have.
 */
export function startSlots(offering: CreatorOfferingView, day: string, hours: number): string[] {
  const open = openDayFor(offering, day)
  if (!open) return []
  const length = Math.max(1, Math.round(hours)) * 60
  const slots: string[] = []
  for (let minutes = open.from; minutes + length <= open.to; minutes += 60) {
    if (minutes % 60 !== 0) continue
    slots.push(`${String(Math.floor(minutes / 60)).padStart(2, '0')}:00`)
  }
  return slots
}

/**
 * The hours THIS line may start at — a room's published window, or the shoot
 * day's own list.
 *
 * One function, so the picker, `pickIssue` and `applyPrefill` cannot disagree
 * about what is on offer. They did once, and the symptom was a prefill the
 * creator then refused.
 *
 * A shoot has no published room hours to measure against: `openDayFor` reads
 * `offering.openDays`, which a crew does not have, so `startSlots` would
 * return an empty list and the picker would look broken. What bounds a shoot
 * is the day's own list — `dayStarts()` — and then whether a photographer
 * declared those hours, which only the server can answer.
 */
export function startChoices(
  offering: CreatorOfferingView,
  pick: CreatorPick,
  day: string,
): readonly string[] {
  if (offering.bookingMode === 'DAY' && isShootDayLevel(pick.variantSlug)) return dayStarts()
  return startSlots(offering, day, pick.qty)
}

/** Whether the brief field is drawn at all, and whether it may be left empty. */
export const needsBrief = (o: CreatorOfferingView) => o.briefMode !== 'NONE'
export const briefRequired = (o: CreatorOfferingView) => o.briefMode === 'REQUIRED'
/**
 * Is this line cut from a film elsewhere in the order (spec §8 · G8)?
 *
 * The one pick field that is about ANOTHER line, and the reason the three
 * predicates below take a pick rather than an offering alone: what a reel is
 * asked for stops being a fact about the catalogue the moment it inherits its
 * material from a film in the same basket.
 */
export const fromFilm = (pick: CreatorPick) => pick.fromFilmLineId !== null
/** The same pair for the raw material — spec §7's "link wymagany przy płatności". */
export const needsSource = (o: CreatorOfferingView, pick: CreatorPick) =>
  o.sourceMode !== 'NONE' && !fromFilm(pick)
export const sourceRequired = (o: CreatorOfferingView, pick: CreatorPick) =>
  o.sourceMode === 'REQUIRED' && !fromFilm(pick)
/** Priced per minute of finished film, so the length is a BAND, not a number. */
export const needsLengthBand = (o: CreatorOfferingView) => o.pricingUnit === 'MINUTE'
/**
 * Spec §8's raw-footage declaration.
 *
 * Only where the material is REQUIRED — montage. An offering that merely
 * accepts references (`OPTIONAL`) has no editing time scaling with them, so
 * demanding a minute count from it would be a question with no consequence.
 */
export const needsFootage = (o: CreatorOfferingView, pick: CreatorPick) =>
  o.sourceMode === 'REQUIRED' && !fromFilm(pick)

/**
 * How much tape this position may carry, and what going over costs.
 *
 * The same two functions the server prices with, so the figure under the box
 * and the figure on the order are one derivation rather than two.
 */
export function footageFor(
  offering: CreatorOfferingView,
  pick: CreatorPick,
  /**
   * The live rules (Cennik → Reguły). Defaulted so a caller outside the order
   * page prices as the site always did — but the order page DOES pass them,
   * because a basket quoting last deploy's rate and a checkout charging this
   * one is the same bug on two screens.
   */
  rules: PricingRuleValues = DEFAULT_PRICING_RULES,
): { limit: number; declared: number; overNet: number } | null {
  if (!needsFootage(offering, pick)) return null
  const band = pick.lengthBand === null ? null : bandAt(pick.lengthBand)
  const limit = footageLimitFor({
    pricingUnit: offering.pricingUnit,
    lengthBandMax: band?.max ?? null,
    quantity: Math.max(1, pick.qty),
    needsSource: true,
    // The ratio differs per category and per level since 14.10 — see
    // `FOOTAGE_RATIOS`. The creator quotes it and the checkout charges it, so
    // both have to ask the same question with the same two slugs.
    offeringSlug: offering.slug,
    ...(pick.variantSlug ? { variantSlug: pick.variantSlug } : {}),
  })
  if (limit === null) return null
  const declared = Number.parseInt(pick.footageMinutes, 10)
  if (!Number.isFinite(declared) || declared < 0) return { limit, declared: 0, overNet: 0 }
  return {
    limit,
    declared,
    overNet: footageOverageNetMinor(
      limit,
      declared,
      ruleValue(rules, 'footage.overage_net_minor_per_minute'),
    ),
  }
}

/**
 * How many units this position is actually charged for.
 *
 * The stepper's number for everything countable; for a film, the band's floor —
 * spec §8's "płatność za dolny próg". `resolveCheckoutLines` derives the same
 * number from the same band on the server, so the estimate on the screen and
 * the price on the order cannot disagree.
 */
/**
 * How many independent POSITIONS this line is (spec §13 · H3).
 *
 * Mirrors `ResolvedLine.pieces` in `@repo/core`: per-piece work with no
 * calendar becomes one track per unit, and everything else is one. A film is
 * ONE however long it runs — it is priced by the minute, so its quantity is a
 * length rather than a count, and reading it here would make a 25-minute film
 * demand a coordinator on its own.
 */
export function pickPieces(offering: CreatorOfferingView, pick: CreatorPick): number {
  const perPiece = offering.pricingUnit === 'PIECE' && offering.bookingMode === 'NONE'
  return perPiece ? Math.max(1, pick.qty) : 1
}

export function pickQuantity(offering: CreatorOfferingView, pick: CreatorPick): number {
  if (needsLengthBand(offering)) {
    const band = pick.lengthBand === null ? null : bandAt(pick.lengthBand)
    return band ? paidMinutesFor(band) : 0
  }
  // A configured shoot is ONE shoot: its price already covers the whole day.
  // `resolveCheckoutLines` freezes the same 1, so the screen and the order
  // cannot disagree about whether two days were bought.
  if (isConfiguredShoot(offering, pick)) return 1
  return hasQuantity(offering) ? Math.max(1, pick.qty) : 1
}

/**
 * Whether this line is a shoot that `/pl/fotograf-operator` configured.
 *
 * The question three controls turn on: the day stepper (a configured shoot is
 * one shoot), the plan picker (changing the plan here would leave a photo count
 * the new plan does not sell) and the line's own summary, which has to say what
 * was actually bought.
 *
 * It asks whether the ANSWERS are there, not whether the offering could have
 * them: a photographer added straight from the creator has no session and is
 * still sold the old way, by the plan's day rate.
 */
export function isConfiguredShoot(offering: CreatorOfferingView, pick: CreatorPick): boolean {
  if (!offering.shoot) return false
  const photos = Number.parseInt(pick.shootPhotos, 10)
  return Boolean(pick.shootSession && pick.shootScope && Number.isFinite(photos) && photos > 0)
}

/**
 * Whether the quantity control means anything.
 *
 * A hired camera is one piece of gear on one term — a stepper there would offer
 * the same body to the same person twice on the same days. Everything else
 * counts: reels by the piece, hours in a room, days on a shoot.
 */
/**
 * Is there a number to step?
 *
 * A hire's quantity IS its window, and a film's is its length band — both are
 * chosen by a different control, and drawing a stepper beside either would be
 * two controls arguing about one number.
 */
export const hasQuantity = (o: CreatorOfferingView) =>
  o.bookingMode !== 'DATE_RANGE' && o.pricingUnit !== 'MINUTE'

/**
 * The label under the quantity control for a dated offering, where the unit is
 * the calendar rather than a thing: three of an hourly block is three hours,
 * three of a shoot day is three days.
 */
export function quantityHint(offering: CreatorOfferingView, pick: CreatorPick): string {
  const variant = offering.variants.find((v) => v.slug === pick.variantSlug)
  if (offering.bookingMode === 'TIME_WINDOW' && variant?.durationMinutes) {
    return formatMinutes(bookedMinutes(offering, pick))
  }
  if (offering.bookingMode === 'DAY') return `${pick.qty} × dzień`
  return ''
}

/**
 * How long a booked block runs — the level's own length times how many of them.
 *
 * The same product `refFrom` closes a `TIME_WINDOW` with, so the end time on
 * the hour buttons is the end time the order will carry. Falls back through the
 * offering's minimum to an hour, which is what the window rule does.
 */
export function bookedMinutes(offering: CreatorOfferingView, pick: CreatorPick): number {
  const variant = offering.variants.find((v) => v.slug === pick.variantSlug)
  const unit = variant?.durationMinutes ?? offering.minBookingMinutes ?? 60
  return unit * Math.max(1, pick.qty)
}

export function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  if (hours === 0) return `${minutes} min`
  if (minutes === 0) return `${hours} h`
  return `${hours} h ${minutes} min`
}

// ── completeness ───────────────────────────────────────────────────────────

/**
 * Why this position has no PRICE yet — or `null` when it has one.
 *
 * The strictly smaller half of {@link pickProblem}, and separating the two is
 * the whole point. "Cannot be ordered" and "cannot be priced" are different
 * questions with different answers, and running them together made the
 * creator's summary lie: a customer who picked Clean and four reels could see
 * `89 zł / rolka` on the card, and the order total beside it did not move,
 * because the brief box was still empty. Prices that visibly do not add up is
 * the worst thing a basket can do.
 *
 * Only four things make an amount genuinely unknowable:
 *
 *   THE VARIANT      it carries the rate; without one there is no number.
 *   THE QUANTITY     nothing is multiplied by nought.
 *   THE HIRE LENGTH  spec §11 · I4 — the length IS the variant, and the price
 *                    is the threshold's, so a line whose variant is not one of
 *                    the five thresholds has no rate to read.
 *   THE LENGTH BAND  spec §8 — a film is priced per minute and the minutes come
 *                    from the band, so no band is no quantity.
 *
 * Everything else `pickProblem` asks for — the brief, the source link, the
 * date, the hour, the city, the footage declaration — changes whether the order
 * can be PLACED, not what it costs. The submit button still refuses on all of
 * them; the arithmetic stops pretending it cannot count.
 */
export function pricingProblem(offering: CreatorOfferingView, pick: CreatorPick): string | null {
  return pricingIssue(offering, pick)?.message ?? null
}

/**
 * Which control a refusal is about.
 *
 * The sentence used to be all a caller got, so the card could only print it at
 * the bottom — under six controls, one of which was the one being complained
 * about. Naming the field is what lets the card mark THAT control and put the
 * sentence beneath it, which is where a person looks.
 *
 * Deliberately the field a person operates, not the pick key it writes:
 * `'date'` covers both ends of a hire, and `'length'` is the slider whether it
 * is a film's band or a hire's term.
 */
export type PickField =
  | 'variant'
  | 'qty'
  | 'date'
  | 'time'
  | 'city'
  | 'brief'
  | 'source'
  | 'footage'
  | 'length'

export type PickIssue = { field: PickField; message: string }

/**
 * Is there a control for this field on this position at all?
 *
 * The card needs it because naming the field is only half the job: a refusal
 * printed on a control that was never drawn is a refusal nobody can see. The
 * one case that really happens is a hire, where `pricingIssue` says `'length'`
 * and there is no length slider — the term is the variant. The card catches
 * that and every other mismatch by asking here before it trusts the pairing.
 */
export function fieldShown(
  offering: CreatorOfferingView,
  pick: CreatorPick,
  field: PickField,
): boolean {
  switch (field) {
    case 'variant':
      return offering.variants.length > 0
    case 'qty':
      return hasQuantity(offering)
    case 'date':
      return needsDate(offering)
    case 'time':
      return needsTime(offering)
    case 'city':
      return needsCity(offering)
    case 'brief':
      return needsBrief(offering)
    case 'source':
      return needsSource(offering, pick)
    case 'footage':
      return needsFootage(offering, pick)
    case 'length':
      return needsLengthBand(offering)
  }
}

/** {@link pricingProblem}, with the control it belongs to. */
export function pricingIssue(offering: CreatorOfferingView, pick: CreatorPick): PickIssue | null {
  const at = (field: PickField, message: string): PickIssue => ({ field, message })

  if (!pick.variantSlug) return at('variant', 'Wybierz wariant.')
  if (!offering.variants.some((v) => v.slug === pick.variantSlug)) {
    return at('variant', 'Wybierz wariant.')
  }
  if (pick.qty < 1) return at('qty', 'Podaj ilość.')

  // Spec §11 · I4: a hire's length is one of five thresholds, and each carries
  // its own price. A line stored before I4 can still name something else.
  if (needsRange(offering) && termOfSlug(pick.variantSlug) === null) {
    return at('length', `Wybierz długość wynajmu — ${RENTAL_TERMS_TEXT}.`)
  }

  // Spec §8's length band. Without it the line is a film of nought minutes,
  // which is not a price — it is the absence of one.
  if (needsLengthBand(offering) && (pick.lengthBand === null || !bandAt(pick.lengthBand))) {
    return at('length', 'Wybierz docelową długość filmu.')
  }

  return null
}

/**
 * Why a position cannot go into the basket yet, in Polish, or `null` when it
 * can. One sentence per position, shown next to the field that is missing —
 * a basket-level "uzupełnij dane" would make the visitor hunt for which card.
 *
 * This mirrors what `resolveCheckoutLines` refuses server-side. The server is
 * the authority; this exists so the refusal happens before the customer has
 * typed their email, not after.
 */
export function pickProblem(offering: CreatorOfferingView, pick: CreatorPick): string | null {
  return pickIssue(offering, pick)?.message ?? null
}

/** {@link pickProblem}, with the control it belongs to — see {@link PickField}. */
export function pickIssue(offering: CreatorOfferingView, pick: CreatorPick): PickIssue | null {
  const at = (field: PickField, message: string): PickIssue => ({ field, message })

  const unpriceable = pricingIssue(offering, pick)
  if (unpriceable) return unpriceable

  if (needsDate(offering)) {
    if (!fromISO(pick.from)) return at('date', 'Wybierz datę.')
    if (needsTime(offering, pick)) {
      if (!/^\d{2}:\d{2}$/.test(pick.time)) return at('time', 'Wybierz godzinę startu.')
      // A shoot is bounded by its PLAN, not by a room's calendar. Whether
      // anybody has declared those hours is the server's question — only it
      // can see every photographer's calendar at once, and it asks inside the
      // payment transaction so two checkouts cannot both take the morning.
      // All this can know for certain is whether the hour is on the list.
      if (offering.bookingMode === 'DAY') {
        if (!startChoices(offering, pick, pick.from).includes(pick.time)) {
          return at('time', 'O tej godzinie nie zaczynamy tego planu — wybierz inną.')
        }
      }
      // Spec §10, mirrored from `slotProblem` / `isWithinOpenHours` on the
      // server. The picker below only offers legal values; this is what catches
      // a line whose day was changed AFTER its hour was chosen — the one way a
      // legal control produces an illegal pair.
      //
      // `else if`, so a shoot day is never measured against ROOM hours: the
      // two questions have different answers and only one of them applies.
      else if (offering.openDays !== null) {
        const open = openDayFor(offering, pick.from)
        if (!open) return at('date', 'Tego dnia studio jest zamknięte — wybierz inny.')
        if (!startSlots(offering, pick.from, pick.qty).includes(pick.time)) {
          return at('time', 'O tej porze nie zmieścimy całej rezerwacji — wybierz inną godzinę.')
        }
      }
    }
  }

  if (needsCity(offering)) {
    if (!pick.citySlug) return at('city', 'Wybierz miasto.')
    if (!offering.citySlugs.includes(pick.citySlug)) {
      return at('city', 'Ta usługa nie dojeżdża do tego miasta.')
    }
  }

  // Spec §6's "brief tekstowy (wymagany)". Refused here so the customer is told
  // beside the empty box rather than after they have typed their email; the
  // server refuses it again in `resolveCheckoutLines`, which is the authority.
  if (briefRequired(offering) && !pick.brief.trim()) {
    return at('brief', 'Opisz, co mamy przygotować.')
  }

  // Spec §7's source link. Same division of labour as the brief: refused beside
  // the empty box here, and refused again by `resolveCheckoutLines`, which is
  // the authority. The shape check is deliberately the loose one — the real
  // test is whether a person can open it.
  if (needsSource(offering, pick) && pick.sourceUrl.trim() && !isSourceUrl(pick.sourceUrl)) {
    return at('source', 'Wklej pełny link (https://…) do materiału.')
  }
  if (sourceRequired(offering, pick) && !pick.sourceUrl.trim()) {
    return at('source', 'Wklej link do materiału źródłowego.')
  }

  // Spec §8: "Klient wpisuje w koszyku długość surówki." Required, because the
  // limit and the fee both hang off it — and a blank box would silently price
  // as nought minutes of tape.
  if (needsFootage(offering, pick)) {
    const declared = Number.parseInt(pick.footageMinutes, 10)
    if (!Number.isFinite(declared) || declared <= 0) {
      return at('footage', 'Podaj, ile minut surówki wysyłasz.')
    }
  }

  return null
}

/** How many days a position occupies — 0 when it books nothing. */
export function pickDays(offering: CreatorOfferingView, pick: CreatorPick): number {
  // Spec §11 · I4: a hire's length is the VARIANT — one of five thresholds —
  // not the span of a calendar. `countDays` is kept as the fallback for a line
  // stored before I4, whose variant is not one of the five.
  if (needsRange(offering))
    return termOfSlug(pick.variantSlug ?? '') ?? countDays(pick.from, pick.to)
  if (offering.bookingMode === 'DAY') return Math.max(1, pick.qty)
  return 0
}

/**
 * The lengths a hire is sold for, priced from the catalogue (spec §11 · I4).
 *
 * Read off the offering's own variants rather than computed, so the creator,
 * the detail page and the checkout all quote the same figure. Empty for
 * anything that is not a hire.
 */
export function rentalTermsOf(offering: CreatorOfferingView): RentalTermView[] {
  if (!needsRange(offering)) return []
  return offering.variants
    .flatMap((v) => {
      const days = termOfSlug(v.slug)
      return days === null || v.quoteOnly
        ? []
        : [{ days, variantSlug: v.slug, label: termLabel(days), priceNet: v.priceNet }]
    })
    .sort((a, b) => a.days - b.days)
}

/**
 * The catalogue reference a complete position becomes — the same shape the
 * checkout posts, so the basket stores what the server will read rather than
 * something it has to be translated into.
 *
 * `TIME_WINDOW` carries `YYYY-MM-DDTHH:MM` on both ends: the end is derived
 * from the variant's own duration times the quantity, which is what stops
 * "pół dnia" from being four hours in one place and a name in another.
 */
export function toCartRef(offering: CreatorOfferingView, pick: CreatorPick): CartLineRef | null {
  if (pickProblem(offering, pick)) return null
  const ref = refFrom({
    offeringSlug: offering.slug,
    variantSlug: pick.variantSlug,
    citySlug: needsCity(offering) ? pick.citySlug : null,
    bookingMode: offering.bookingMode,
    from: pick.from,
    to: pick.to,
    time: pick.time,
    qty: pick.qty,
    durationMinutes:
      offering.variants.find((v) => v.slug === pick.variantSlug)?.durationMinutes ?? null,
    minBookingMinutes: offering.minBookingMinutes,
    lengthBand: needsLengthBand(offering) ? pick.lengthBand : null,
    fromFilmLineId: pick.fromFilmLineId,
    recordingType: pick.recordingType,
  })
  // Attached AFTER the window rule, never inside it. `refFrom` is also what
  // `cartLineId` reads, and the brief is a DESCRIPTION of a line rather than
  // part of its identity — see `cart_item.brief` for why that distinction
  // matters to de-duplication.
  const brief = needsBrief(offering) ? pick.brief.trim() : ''
  const sourceUrl = needsSource(offering, pick) ? pick.sourceUrl.trim() : ''
  const footage = needsFootage(offering, pick)
    ? Number.parseInt(pick.footageMinutes, 10)
    : Number.NaN
  return {
    ...ref,
    ...(brief ? { brief } : {}),
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(Number.isFinite(footage) && footage > 0 ? { footageMinutes: footage } : {}),
  }
}

/** Everything the window rule needs, and nothing that only a rendered view has. */
export type RefInput = {
  offeringSlug: string
  variantSlug: string | null
  /** The film's length band, which is part of what the line IS (spec §8). */
  lengthBand?: number | null
  /** The film this line is cut from — also part of what it IS (spec §8 · G8). */
  fromFilmLineId?: string | null
  /** What the shoot is — part of what the line IS. See `CartLineRef`. */
  shootSession?: string | null
  shootScope?: string | null
  shootPhotos?: number | null
  /** …and what a recording day is. Same rule, one field. */
  recordingType?: string | null
  citySlug: string | null
  bookingMode: BookingMode
  from: string
  to: string
  time: string
  qty: number
  durationMinutes: number | null
  minBookingMinutes: number | null
}

/**
 * The window rule itself, over PRIMITIVES.
 *
 * Split out of `toCartRef` because the server has to reach the same answer from
 * a database row, and a rule this load-bearing must not exist in two spellings:
 * `from`/`to` decide what a line is worth, which day it holds and whether two
 * lines are the same line. `toCartRef` is now the adapter that knows about a
 * rendered offering; this is the rule.
 *
 * Completeness is deliberately NOT checked here — `toCartRef` gates on
 * `pickProblem` before calling, and the server gates on its own equivalent.
 */
export function refFrom(input: RefInput): CartLineRef {
  const base: CartLineRef = { offeringSlug: input.offeringSlug }
  if (input.variantSlug) base.variantSlug = input.variantSlug
  if (input.citySlug) base.citySlug = input.citySlug
  // INSIDE the identity, unlike the brief and the link beside it. A 20–30
  // minute film and a 60–70 minute one are different products at different
  // prices — merging them into one basket line the way two identical
  // thumbnails merge would silently reprice one of them.
  if (input.lengthBand !== null && input.lengthBand !== undefined) {
    base.lengthBand = input.lengthBand
  }
  // Inside the identity for the same reason (spec §8 · G8): a reel cut from a
  // film in this basket carries no footage fee and cannot start until the film
  // is accepted. A reel cut from the client's own tape carries both. Merging
  // the two would drop one of them.
  if (input.fromFilmLineId) base.fromFilmLineId = input.fromFilmLineId
  // Same rule, third time: what the shoot IS decides what the line is. All
  // three or none — two of them cannot be priced and would only make one line
  // look different from another without being able to say how.
  if (input.shootSession && input.shootScope && input.shootPhotos) {
    base.shootSession = input.shootSession
    base.shootScope = input.shootScope
    base.shootPhotos = input.shootPhotos
  }
  // …and the operator's one answer, on its own terms: it is the whole
  // configuration of his day, so it needs nothing beside it to be complete.
  if (input.recordingType) base.recordingType = input.recordingType

  switch (input.bookingMode) {
    case 'NONE':
      return base
    case 'DATE_RANGE': {
      // The RETURN date is derived from the threshold, never read off the
      // form: spec §11 sells five lengths, so a window is a start plus one of
      // them. The server derives the same number from the same variant and
      // refuses the pair if they disagree.
      const days = termOfSlug(input.variantSlug ?? '') ?? countDays(input.from, input.to)
      return { ...base, from: input.from, to: lastHeldDay(input.from, days), days }
    }
    case 'DAY': {
      const days = Math.max(1, input.qty)
      // ── A SHOOT DAY CARRIES ITS HOUR ─────────────────────────────────
      // `/pl/fotograf-operator` has always asked for a start time and this
      // line always threw it away, so the booking went in all-day and
      // `windowFor` handed the availability check a midnight-to-midnight
      // span — one no declared window can ever cover. The shoot was the only
      // job with real hours and the only one the new check could not place.
      //
      // The pair becomes the SAME `YYYY-MM-DDTHH:MM` shape a studio hour
      // already uses, so `orderLinePeriod` builds a real `clockPeriod` and
      // nothing downstream needed a new case: the day, the overlap test and
      // the declared-hours test all fall out of it.
      //
      // One day only. A multi-day shoot has no single start hour, and
      // inventing one would put eight hours on the first day and nothing on
      // the rest.
      if (isShootDayLevel(input.variantSlug ?? null) && days === 1 && input.time) {
        return {
          ...base,
          from: `${input.from}T${input.time}`,
          to: addMinutes(input.from, input.time, SHOOT_DAY_HOURS * 60),
          days,
        }
      }
      return { ...base, from: input.from, to: lastHeldDay(input.from, days), days }
    }
    case 'TIME_WINDOW': {
      const minutes =
        (input.durationMinutes ?? input.minBookingMinutes ?? 60) * Math.max(1, input.qty)
      return {
        ...base,
        from: `${input.from}T${input.time}`,
        to: addMinutes(input.from, input.time, minutes),
      }
    }
  }
}

/** `YYYY-MM-DDTHH:MM` plus minutes, rolling into the next day when it has to. */
export function addMinutes(day: string, time: string, minutes: number): string {
  const date = fromISO(day)
  const [h, m] = time.split(':').map(Number)
  if (!date || h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return ''
  const total = h * 60 + m + minutes
  const dayShift = Math.floor(total / 1440)
  const rest = ((total % 1440) + 1440) % 1440
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${addDaysISO(day, dayShift)}T${pad(Math.floor(rest / 60))}:${pad(rest % 60)}`
}

// ── deep links ─────────────────────────────────────────────────────────────

/**
 * The query the offer pages hand off with. Polish keys, because they show up in
 * the address bar of a Polish site and a shared link is read by a person.
 */
export const CREATOR_PARAMS = {
  offering: 'oferta',
  variant: 'pakiet',
  qty: 'ile',
  from: 'od',
  to: 'do',
  city: 'miasto',
  /** `HH:MM` start, for `TIME_WINDOW` rows — see the note on `applyPrefill`. */
  time: 'godz',
  /**
   * A sentence the offer page already asked for, seeded into the line's brief.
   *
   * `/pl/strategia` asks how many people are coming and used to keep the
   * answer in local state — it printed it in the „Twój wybór" summary and then
   * dropped it on the way to the creator, so a question that reads like part
   * of the order was decoration and FLOU had to ask again.
   */
  brief: 'brief',
  /** The shoot's three, from `/pl/fotograf-operator`. See `CreatorPick`. */
  shootSession: 'sesja',
  shootScope: 'zakres',
  shootPhotos: 'zdjecia',
} as const

export type CreatorPrefill = {
  offeringSlug?: string
  variantSlug?: string
  qty?: number
  from?: string
  to?: string
  citySlug?: string
  /** Start hour a booking page already asked for. Dropped if the room is shut. */
  time?: string
  /** Seeds the line's brief. Dropped where the offering has no brief field. */
  brief?: string
  /** The shoot's configuration, as `/pl/fotograf-operator` resolved it. */
  shootSession?: string
  shootScope?: string
  shootPhotos?: number
}

/** Build the link an offer page's buy button points at. */
export function creatorHref(locale: string, prefill: CreatorPrefill): string {
  const query = new URLSearchParams()
  if (prefill.offeringSlug) query.set(CREATOR_PARAMS.offering, prefill.offeringSlug)
  if (prefill.variantSlug) query.set(CREATOR_PARAMS.variant, prefill.variantSlug)
  if (prefill.qty && prefill.qty > 1) query.set(CREATOR_PARAMS.qty, String(prefill.qty))
  if (prefill.from) query.set(CREATOR_PARAMS.from, prefill.from)
  if (prefill.to) query.set(CREATOR_PARAMS.to, prefill.to)
  if (prefill.citySlug) query.set(CREATOR_PARAMS.city, prefill.citySlug)
  if (prefill.time) query.set(CREATOR_PARAMS.time, prefill.time)
  if (prefill.brief) query.set(CREATOR_PARAMS.brief, prefill.brief)
  // All three or none: two of them cannot be priced, and a link carrying half a
  // shoot would land in the basket as a day rate with a session name on it.
  if (prefill.shootSession && prefill.shootScope && prefill.shootPhotos) {
    query.set(CREATOR_PARAMS.shootSession, prefill.shootSession)
    query.set(CREATOR_PARAMS.shootScope, prefill.shootScope)
    query.set(CREATOR_PARAMS.shootPhotos, String(prefill.shootPhotos))
  }
  const search = query.toString()
  // The anchor lands the reader on the card that was just preselected rather
  // than at the top of a page whose point is three screens down.
  return `/${locale}/kreator${search ? `?${search}#pozycje` : ''}`
}

/**
 * Read a deep link. Everything is optional and nothing is trusted: an unknown
 * slug simply selects nothing, because a link shared in March must not 404 a
 * page in June when the offering behind it was renamed.
 */
export function readPrefill(params: Record<string, string | string[] | undefined>): CreatorPrefill {
  const one = (key: string): string | undefined => {
    const raw = params[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    return value?.trim() ? value.trim().slice(0, 64) : undefined
  }
  const qty = Number(one(CREATOR_PARAMS.qty))
  const photos = Number(one(CREATOR_PARAMS.shootPhotos))
  return {
    ...(one(CREATOR_PARAMS.offering) ? { offeringSlug: one(CREATOR_PARAMS.offering) } : {}),
    ...(one(CREATOR_PARAMS.variant) ? { variantSlug: one(CREATOR_PARAMS.variant) } : {}),
    ...(Number.isInteger(qty) && qty > 0 && qty <= 9999 ? { qty } : {}),
    ...(one(CREATOR_PARAMS.from) ? { from: one(CREATOR_PARAMS.from) } : {}),
    ...(one(CREATOR_PARAMS.to) ? { to: one(CREATOR_PARAMS.to) } : {}),
    ...(one(CREATOR_PARAMS.city) ? { citySlug: one(CREATOR_PARAMS.city) } : {}),
    ...(one(CREATOR_PARAMS.time) ? { time: one(CREATOR_PARAMS.time) } : {}),
    ...(one(CREATOR_PARAMS.brief) ? { brief: one(CREATOR_PARAMS.brief) } : {}),
    ...(one(CREATOR_PARAMS.shootSession) &&
    one(CREATOR_PARAMS.shootScope) &&
    Number.isInteger(photos) &&
    photos > 0 &&
    photos <= 999
      ? {
          shootSession: one(CREATOR_PARAMS.shootSession),
          shootScope: one(CREATOR_PARAMS.shootScope),
          shootPhotos: photos,
        }
      : {}),
  }
}

/** Apply a deep link to a fresh position, ignoring anything that does not fit. */
export function applyPrefill(offering: CreatorOfferingView, prefill: CreatorPrefill): CreatorPick {
  const pick = emptyPick(offering)
  const variant = offering.variants.find((v) => v.slug === prefill.variantSlug)
  if (variant) {
    pick.variantSlug = variant.slug
    pick.qty = defaultQtyFor(offering, variant)
  }
  if (prefill.qty) pick.qty = prefill.qty
  if (prefill.from && fromISO(prefill.from)) pick.from = prefill.from
  if (prefill.to && fromISO(prefill.to)) pick.to = prefill.to
  if (prefill.citySlug && offering.citySlugs.includes(prefill.citySlug)) {
    pick.citySlug = prefill.citySlug
  }
  // Only where the offering HAS a brief box. Seeding a field the creator never
  // draws would put a sentence on the line that nobody could see or correct —
  // and `readPrefill` already caps the length, so this cannot smuggle an essay
  // past `BRIEF_MAX_CHARS`.
  if (prefill.brief && needsBrief(offering)) pick.brief = prefill.brief
  // Checked against THIS offering's own model, not trusted off the URL: a link
  // naming a session this role does not sell, or a row that is not on its plan,
  // is dropped and the line falls back to the plan's rate — which is the same
  // thing `applyPrefill` does with a city nobody covers.
  if (
    prefill.shootSession &&
    prefill.shootScope &&
    prefill.shootPhotos &&
    offering.shoot?.scopes.some(
      (scope) => scope.session === prefill.shootSession && scope.value === prefill.shootScope,
    )
  ) {
    pick.shootSession = prefill.shootSession
    pick.shootScope = prefill.shootScope
    pick.shootPhotos = String(prefill.shootPhotos)
  }
  /**
   * Last, and only if the room is genuinely open then.
   *
   * `/pl/studio` now asks for the hour before handing off, using the same
   * calendar `/pl/fotograf-operator` uses — and that calendar offers a fixed 09:00–16:00
   * because it knows nothing about any one room's published days. A room can be
   * shut at the hour it offered, so the value is checked against the SAME
   * predicate the creator validates with (`startSlots`) instead of being
   * trusted: an hour that does not fit is dropped and the creator asks for one,
   * which is this function's stated contract — "ignoring anything that does not
   * fit" — rather than a prefill that walks the reader into a refusal.
   *
   * It runs after the quantity is settled because `startSlots` measures whether
   * the WHOLE booking fits before closing time, and the quantity is its length.
   */
  if (prefill.time && pick.from && startChoices(offering, pick, pick.from).includes(prefill.time)) {
    pick.time = prefill.time
  }
  return pick
}
